import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";

export const fetchProjectDetails = (projectId: number, setProject: Function, setLoaded: Function, language: string) => {
  setLoaded(false);
  ApiService.get(`projects/${projectId}`)
    .then((response) => setProject(response.data.project))
    .catch((err) => {
      console.error("Error while loading project:", err);
      alert(translate(language, "PAGES.PROJECT.ERROR.FETCH_PROJECT"));
    })
    .finally(() => setLoaded(true));
};

export const fetchProjectMeetings = (
  projectId: number,
  setMeetings: Function,
  setLoaded: Function,
  language: string
) => {
  setLoaded(false);
  ApiService.get(`projects/${projectId}/meetings`)
    .then((response) => setMeetings(response.data.meetings))
    .catch((err) => {
      console.error("Error while loading project meetings:", err);
      alert(translate(language, "PAGES.PROJECT.ERROR.FETCH_MEETINGS"));
    })
    .finally(() => setLoaded(true));
};

export const fetchProjectUsers = (projectId: number, setUsers: Function, setLoaded: Function, language: string) => {
  setLoaded(false);
  ApiService.get(`projects/${projectId}/users?fetch_user_flags=true`)
    .then((response) => setUsers(response.data.users))
    .catch((err) => {
      console.error("Error while loading project users:", err);
      alert(translate(language, "PAGES.PROJECT.ERROR.FETCH_USERS"));
    })
    .finally(() => setLoaded(true));
};

export const fetchProjectSubjects = (setSubjects: Function, setLoading: Function, language: string) => {
  setLoading(true);
  ApiService.get("projects/subjects")
    .then((response) => setSubjects(response.data.project_subjects))
    .catch((error) => {
      console.error("Error fetching project subjects", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.FETCH_PROJECT_SUBJECTS"));
    })
    .finally(() => setLoading(false));
};

export const addUsersToProject = (
  projectId: number,
  userIds: number[],
  setLoading: Function,
  onSuccess: Function,
  language: string
) => {
  setLoading(true);
  ApiService.post(`projects/${projectId}/users`, {
    user_ids: userIds,
  })
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error adding users to project:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.ADD_USERS"));
    })
    .finally(() => setLoading(false));
};

export const removeUsersFromProject = (
  projectId: number,
  userIds: number[],
  setLoading: Function,
  onSuccess: Function,
  language: string
) => {
  setLoading(true);

  ApiService.delete(`projects/${projectId}/users`, {
    user_ids: userIds,
  })
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error removing users from project:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.DELETE_USERS"));
    })
    .finally(() => setLoading(false));
};

export const updateProjectUserScore = (
  projectId: number,
  userId: number,
  score: number,
  scoreBase: number,
  language: string,
  onSuccess: Function,
  setLoading: Function
) => {
  setLoading(true);
  ApiService.put(`projects/${projectId}/users/${userId}`, {
    score,
    score_base: scoreBase,
  })
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error updating user score:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.UPDATE_SCORE"));
    })
    .finally(() => setLoading(false));
};

export const updateProject = (
  projectId: number,
  data: any,
  setLoading: Function,
  onSuccess: Function,
  language: string
) => {
  setLoading(true);
  ApiService.put(`projects/${projectId}`, data)
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error updating project:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.UPDATE_PROJECT"));
    })
    .finally(() => setLoading(false));
};

export const fetchProjectSubjectResources = (
  subjectId: number,
  setResources: Function,
  setLoaded: Function,
  language: string
) => {
  setLoaded(false);
  ApiService.get(`projects/subjects/${subjectId}/resources`)
    .then((response) => setResources(response.data.project_subject_resources))
    .catch((err) => {
      console.error("Error while fetching project subject resources:", err);
      alert(translate(language, "PAGES.PROJECT.ERROR.FETCH_RESOURCES"));
    })
    .finally(() => setLoaded(true));
};

export const fetchProjectNotes = (projectId: number, setNotes: Function, setLoaded: Function, language: string) => {
  setLoaded(false);
  ApiService.get(`projects/${projectId}/notes?fetch_author=true`)
    .then((response) => setNotes(response.data.notes))
    .catch((error) => {
      console.error("Error fetching project notes:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.FETCH_NOTES"));
    })
    .finally(() => setLoaded(true));
};

export const createProjectNote = (
  projectId: number,
  data: {
    value: string;
    rating?: number;
    is_pinned?: boolean;
  },
  setLoading: Function,
  onSuccess: Function,
  language: string
) => {
  setLoading(true);
  ApiService.post(`projects/${projectId}/notes`, data)
    .then((response) => onSuccess(response.data.notes))
    .catch((error) => {
      console.error("Error creating project note:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.CREATE_NOTE"));
    })
    .finally(() => setLoading(false));
};

export const updateProjectNote = (
  projectId: number,
  noteId: string,
  data: {
    content: string;
    rating?: number;
    is_pinned?: boolean;
  },
  language: string,
  onSuccess: Function
) => {
  ApiService.put(`projects/${projectId}/notes/${noteId}`, data)
    .then((response) => onSuccess(response.data.note))
    .catch((error) => {
      console.error("Error updating project note:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.UPDATE_NOTE"));
    });
};

export const deleteProjectNote = (projectId: number, noteId: string, language: string, onSuccess: Function) => {
  ApiService.delete(`projects/${projectId}/notes/${noteId}`)
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error deleting project note:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.DELETE_NOTE"));
    });
};

export const deleteProject = (projectId: number, language: string, onSuccess: Function) => {
  ApiService.delete(`projects/${projectId}`)
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error deleting project:", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.DELETE_PROJECT"));
    });
};

export const addModulesToProjectMembers = (
  projectId: number,
  moduleIds: number[],
  language: string,
  setLoading: Function,
  onSuccess: Function
) => {
  setLoading(true);

  ApiService.post(`projects/${projectId}/members/modules`, { module_ids: moduleIds })
    .then(() => onSuccess())
    .catch((error) => {
      console.error("Error adding modules to project members", error);
      alert(translate(language, "PAGES.PROJECT.ERROR.ADD_MODULES"));
    })
    .finally(() => setLoading(false));
};
